import { UnauthorizedResponse, UserRoleType } from '@order-management/types';
import { AxiosError } from 'axios';
import { MouseEvent, ReactNode, useCallback, useMemo, useState } from 'react';
import { Element } from 'react-bulma-components';
import { useNavigate } from 'react-router-dom';
import { logout } from '../../../shared/auth-service';
import Conditional from '../../../shared/conditional/conditional';
import useHttpRequest from '../../../shared/hooks/http-request';
import useIsCustomerUser from '../../../shared/hooks/is-customer-user';
import useIsZspaceInternalUser from '../../../shared/hooks/is-zspace-internal-user';
import useToast from '../../../shared/hooks/toasts';
import useUser from '../../../shared/hooks/user';
import useUserHasZspaceInternalAndCustomerRoles from '../../../shared/hooks/user-has-zspace-internal-and-customer-roles';
import If from '../../../shared/if/if';
import { UserInitializedStorageService } from '../../../users/user-initialized-storage-service';
import { switchRole } from '../../../users/users-service';
import SwitchToCustomerRoleModal from '../../../zspace-internal/switch-to-customer-role-modal/switch-to-customer-role-modal';
import DropdownMenu from '../../dropdown-menu/dropdown-menu';
import Spinner from '../../spinner/spinner';

const SWITCH_ROLE_ERROR_MESSAGE = 'Could not switch role. Please try again';

const userInitializedStorageService = new UserInitializedStorageService();

export type LoadingAction = {
  switchRole?: boolean;
  logout?: boolean;
};

export function ProfileActions() {
  const { user } = useUser();
  const isZspaceInternalUser = useIsZspaceInternalUser();
  const isCustomerUser = useIsCustomerUser();
  const hasZspaceInternalAndCustomerRoles =
    useUserHasZspaceInternalAndCustomerRoles();
  const toast = useToast();
  const navigate = useNavigate();
  const [isLoadingAction, setIsLoadingAction] = useState<LoadingAction>({
    logout: false,
  });
  const { executeHttpRequest, isLoading: isSwitchingRole } = useHttpRequest();
  const [showSwitchRoleModal, setShowSwitchRoleModal] =
    useState<boolean>(false);

  const switchToRoleText = useMemo(() => {
    return isZspaceInternalUser
      ? 'Manage my customer’s orders'
      : 'Switch to zSpace role';
  }, [isZspaceInternalUser]);

  const handleLogout = useCallback(
    async (event: MouseEvent<HTMLAnchorElement>) => {
      try {
        setIsLoadingAction({ logout: true });
        event.stopPropagation();
        const logoutResponse = await logout();
        const loginUrl = logoutResponse.authUrl;
        if (loginUrl) {
          window.location.assign(loginUrl);
        }
        userInitializedStorageService.markAsNotInitialized();
        setIsLoadingAction({ logout: false });
      } catch (error) {
        setIsLoadingAction({ logout: false });
        if (error instanceof AxiosError) {
          const loginUrl = (error as AxiosError<UnauthorizedResponse>).response
            ?.data?.authUrl;
          if (loginUrl) {
            window.location.assign(loginUrl);
          } else {
            toast.error('Could not logout. Please try again');
          }
        }
      }
    },
    [toast]
  );

  const navigateToSelectTemporaryPermissionsPage = useCallback(() => {
    setShowSwitchRoleModal(false);
    navigate('customer-management');
  }, [navigate]);

  const handleSwitchToInternalRole = useCallback(async () => {
    return executeHttpRequest({
      asyncFunction: async () => {
        const userRoleToSwitch = user.roles.find(
          (roleAssignment) =>
            roleAssignment.role.name === UserRoleType.ZSPACE_INTERNAL
        );
        await switchRole(userRoleToSwitch!.id);
        window.location.replace('/');
      },
      customErrorMessage: SWITCH_ROLE_ERROR_MESSAGE,
    });
  }, [executeHttpRequest, user.roles]);

  const onSwitchRoleClick = useCallback(
    async (event: MouseEvent<HTMLAnchorElement>) => {
      event.stopPropagation();
      if (isZspaceInternalUser) {
        setShowSwitchRoleModal(true);
      } else {
        handleSwitchToInternalRole();
      }
    },
    [handleSwitchToInternalRole, isZspaceInternalUser]
  );

  const navigateToUserDetails = useCallback(() => {
    navigate(`users/${user.id}`);
  }, [navigate, user.id]);

  return (
    <>
      <If condition={hasZspaceInternalAndCustomerRoles}>
        <DropdownMenu.Item
          onClick={onSwitchRoleClick}
          stopEventPropagationOnClick={false}
        >
          <Conditional condition={isCustomerUser}>
            <Conditional.True>
              <LoadingElement loading={!!isSwitchingRole}>
                {switchToRoleText}
              </LoadingElement>
            </Conditional.True>
            <Conditional.False>{switchToRoleText}</Conditional.False>
          </Conditional>
        </DropdownMenu.Item>
      </If>
      <If condition={isCustomerUser || isZspaceInternalUser}>
        <DropdownMenu.Item
          onClick={navigateToUserDetails}
          stopEventPropagationOnClick={false}
        >
          Account
        </DropdownMenu.Item>
      </If>
      <DropdownMenu.Item
        onClick={handleLogout}
        stopEventPropagationOnClick={false}
      >
        <LoadingElement loading={!!isLoadingAction.logout}>
          Logout
        </LoadingElement>
      </DropdownMenu.Item>
      <SwitchToCustomerRoleModal
        show={showSwitchRoleModal}
        onClose={() => setShowSwitchRoleModal(false)}
        onSwitchRole={navigateToSelectTemporaryPermissionsPage}
      />
    </>
  );
}

function LoadingElement({
  children,
  loading,
}: {
  children: ReactNode;
  loading: boolean;
}) {
  return (
    <Element display="flex" alignItems="center" className="gap-5">
      {children}
      <Spinner style={{ opacity: loading ? 1 : 0 }} size="small" />
    </Element>
  );
}

export default ProfileActions;
